import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { StoredTarget, TargetBasis } from '../types/taTracking';

/**
 * The trimester's committed hiring target.
 *
 * ⚠️ This replaces `services/taCockpitBaseline.ts`, which wrote the target to
 * localStorage under a comment promising it was "shared with the team". It was
 * not — it existed only in the browser that committed it, which is why the
 * Tracking tab's Planning Accuracy chart appeared blank to everyone else.
 */
const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

const base = (period: string) =>
  `${PROXY_URL}/api/ta-demand/${encodeURIComponent(period)}/target`;

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error || body?.detail || '';
    } catch {
      /* a non-JSON error body tells us nothing useful */
    }
    throw new Error(detail || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export function fetchCommittedTarget(period: string): Promise<{ target: StoredTarget | null }> {
  return authenticatedFetch(base(period)).then((r) => json<{ target: StoredTarget | null }>(r));
}

export function commitCommittedTarget(
  period: string,
  body: { target: number; note?: string; basis?: TargetBasis },
): Promise<{ target: StoredTarget }> {
  return authenticatedFetch(base(period), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => json<{ target: StoredTarget }>(r));
}
